import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';
import useAuth from '../../auth/feature/auth';

const API_ORIGIN =
  process.env.REACT_APP_BUILD_ENV === 'development'
    ? process.env.REACT_APP_BACKEND_URL_DEV
    : process.env.REACT_APP_BACKEND_URL;

/**
 * Keeps an admin's grant or revoke visible to the account it applies to,
 * without them reloading.
 *
 * The socket only signals that something moved. It carries no entitlement and
 * nothing is trusted from it — the response is to re-fetch through the normal
 * gated endpoints, so access is still decided server-side on a fresh request.
 * That also means a dropped connection degrades to exactly the behaviour we
 * had before: the change lands on the next page load.
 *
 * Invalidating ['my-access'] is enough to move everything that depends on a
 * grant, because they all read that one query: the products dropdown, the
 * header's transparency rule and the Standby route guard.
 */
export const useAccessRealtime = (): void => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isAuthenticated || !API_ORIGIN) return undefined;

    const socket = io(`${API_ORIGIN}/realtime`, {
      // The auth cookie is HttpOnly, so the browser attaches it and the
      // gateway reads it off the handshake — nothing to pass explicitly.
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    const onAccessChanged = () => {
      queryClient.invalidateQueries({ queryKey: ['my-access'] });
      // The Standby page's content is gated too, so a revoke has to drop it
      // rather than leave the last successful fetch on screen.
      queryClient.invalidateQueries({ queryKey: ['standby-page'] });
      // Riser's page is fetched from behind its grant check, so a fresh grant
      // has to re-ask for it. Without this the menu entry and the page would
      // both stay empty until a reload, which for a product nobody can see
      // otherwise reads as the grant not having worked.
      queryClient.invalidateQueries({ queryKey: ['riser-page'] });
      // The framework grant is the one that also CHANGES THE KEY LIST: granting
      // it links the account to a LicenseSpring 'mix' licence (adopted or newly
      // minted) server-side, so My Products has a key to show that it did not
      // have a moment ago. Without this the card would appear with an empty
      // licence table until a reload.
      queryClient.invalidateQueries({ queryKey: ['keys'] });
      queryClient.invalidateQueries({ queryKey: ['sbframework-page'] });
      queryClient.invalidateQueries({ queryKey: ['sbframework-eula'] });
    };

    // An admin moving a licence's activation limit. Separate event from
    // access:changed because it means something different - the entitlement did
    // not move, what the licence permits did - and only the key list needs to
    // re-read. Same discipline: the payload is a signal, the numbers come from
    // a fresh authenticated fetch.
    const onLicenseChanged = () => {
      queryClient.invalidateQueries({ queryKey: ['keys'] });
      queryClient.invalidateQueries({ queryKey: ['my-licenses'] });
    };

    socket.on('access:changed', onAccessChanged);
    socket.on('license:changed', onLicenseChanged);

    return () => {
      socket.off('access:changed', onAccessChanged);
      socket.off('license:changed', onLicenseChanged);
      socket.disconnect();
    };
  }, [isAuthenticated, queryClient]);
};

export default useAccessRealtime;
